import { get, post, put, getToken } from "../axiosApi";
import { env } from "utils/env";
import { objToQueryString } from "utils/functions";

const url = env.REACT_APP_LEASING_API_URL; //"http://kube-w1:32751/api";

class OfferApi {
  static getAllOffers(filters) {
    return get(url + "/leasingoffers?" + filters);
  }

  static getOfferHeader(documentId, siteId) {
    return get(`${url}/leasingoffers/${documentId}/${siteId}`);
  }

  static getOfferFinancialProductRuleReadOnlyFields(documentId, siteId) {
    return get(`${url}/leasingoffers/${documentId}/${siteId}/financialproductruleoutputdata`);
  }

  static createOffer(offer) {
    return post(url + "/leasingoffers/offer-with-asset", offer);
  }

  static computeOfferFinancialProductList(documentId, siteId, financingPeriod, advancePercent, residualValuePercent) {
    return post(`${url}/leasingoffers/financialproductlist/compute`, {
      documentId,
      siteId,
      financingPeriod,
      advancePercent,
      residualValuePercent,
      includeServices: true
    });
  }

  static updateOfferProduct(
    documentId,
    siteId,
    productId,
    periodMonths,
    advancePercent,
    residualValuePercent,
    maxPeriod
  ) {
    return put(url + `/leasingoffers/${documentId}/${siteId}/financialproduct`, {
      documentId,
      siteId,
      productId,
      periodMonths,
      advancePercent,
      residualValuePercent,
      maxPeriod
    });
  }

  static getOfferConfig(documentTypeId) {
    return get(`${url}/leasingoffers/${documentTypeId}/config`);
  }

  static loadUserPreferences() {
    return get(`${url}/leasingoffers/user-preferences`);
  }

  static updateUserPreferences(userPreferences) {
    var newUserPreferences = userPreferences.reduce((items, item) => {
      if (!item.isDefault) {
        const newItem = { filterName: item.filterName, preferenceValue: JSON.stringify(item) };
        items.push(newItem);
      }
      return items;
    }, []);

    return post(`${url}/leasingoffers/update-user-preferences`, { userPreferences: newUserPreferences });
  }

  static submitOfferPreApproval(documentId, siteId) {
    return post(`${url}/leasingoffers/${documentId}/${siteId}/preapproval`, { documentId, siteId });
  }

  static computeOfferNextStatesList(documentId, siteId) {
    return post(`${url}/leasingoffers/${documentId}/${siteId}/states/compute`, { documentId, siteId });
  }

  static updateOfferState(documentId, siteId, stateId, comment, reasonsIds) {
    return post(url + `/leasingoffers/${documentId}/${siteId}/state`, {
      documentId,
      siteId,
      stateId,
      comment,
      selectedReasons: reasonsIds
    });
  }

  static getCommentsConfig(documentTypeId) {
    return get(url + `/leasingoffers/commentsconfig/${documentTypeId}`);
  }

  static getFolderList(documentId, siteId, stateId) {
    return post(url + `/leasingOffers/${documentId}/${siteId}/folderList`, { documentId, siteId, stateId });
  }

  static getDocumentFileLocation(documentId, siteId, fileId) {
    return post(`${url}/leasingoffers/${documentId}/${siteId}/uploadDocuments/${fileId}/location`);
  }

  static loadOfferDecisionSelectedConditions(documentId, siteId) {
    return get(`${url}/leasingoffers/${documentId}/${siteId}/selectedconditions`);
  }

  static loadOfferDecisionAvailableConditions() {
    return get(`${url}/leasingoffers/decision/availableconditions`);
  }

  static loadDecisionSubsequentConditions(documentId, siteId) {
    return get(`${url}/leasingoffers/${documentId}/${siteId}/decision/subsequentconditions`);
  }

  static cloneOffer(data) {
    return post(url + `/leasingoffers/${data.documentId}/${data.siteId}/clone/`, data);
  }

  static copyOffer(data) {
    return post(url + `/leasingoffers/${data.documentId}/${data.siteId}/clone-leasing-offer/`, data);
  }

  static updateOfferDecisionSelectedConditions(documentId, siteId, decisionSelectedConditions) {
    return put(url + `/leasingoffers/${documentId}/${siteId}/conditions`, {
      documentId,
      siteId,
      decisionSelectedConditions
    });
  }

  static updateOfferDecisionSubsequentConditions(documentId, siteId, decisionSubsequentConditions) {
    return put(url + `/leasingoffers/${documentId}/${siteId}/decision/subsequentconditions`, {
      documentId,
      siteId,
      decisionSubsequentConditions
    });
  }

  static loadFinancedAssets(documentId, siteId) {
    return get(`${url}/v2/leasingoffer/${documentId}/${siteId}/decision/assets`);
  }

  static loadFinancedChart(documentId, siteId) {
    return get(`${url}/leasingoffers/${documentId}/${siteId}/financedChart`);
  }

  static getOfferWarnings(documentId, siteId) {
    return get(`${url}/leasingoffers/${documentId}/${siteId}/warnings`);
  }

  static getOfferReports(documentId, siteId) {
    return post(`${url}/reports/document-reports`, { documentId, siteId });
  }

  static generateDocumentReport(report) {
    return post(`${url}/reports/generate-document-report`, report);
  }

  static buildOfferExportDownloadUrl(filePath, fileName) {
    return `${url}/leasingoffers/exportsOffers/file/${encodeURIComponent(filePath)}/${encodeURIComponent(
      fileName
    )}?access_token=${getToken()}`;
  }

  static exportOffers(filters, fieldsToShow) {
    return post(`${url}/leasingoffers/exportOffers/list`, { ...filters, fieldsToShow });
  }

  static selectFolder(documentId, siteId, folderId, folderSiteId) {
    return post(`${url}/leasingoffers/${documentId}/${siteId}/updateOfferFolder`, {
      documentId,
      siteId,
      folderId,
      folderSiteId
    });
  }

  static getResponsibleChangeOffers(filters, page, pageSize) {
    return get(url + "/leasingoffers?" + objToQueryString({ ...filters, page, pageSize }));
  }

  static saveResponsibleToOffers(responsibleId, responsibleName, offers) {
    return post(`${url}/leasingoffers/offersResponsibleChange`, { responsibleId, responsibleName, offers: offers });
  }

  static getNavigationStepWarnings(documentId, siteId, navigationStep) {
    return get(`${url}/leasingoffers/${documentId}/${siteId}/${navigationStep}/navigation-step-warnings`);
  }
}

export default OfferApi;
